import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Download, Printer, Save, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import API from '@/config/api';
// API imported from config;

// Official DCF orientation checklist items
const ORIENTATION_ITEMS = [
  "A review of DCF 251, Licensing Rules for Group Child Care Centers 251.05(4)(a)1.",
  "A review of center policies and procedures required under s. DCF 251.04(2)(h) [251.05(4)(a)2.]",
  "A review of the center emergency plans required under s. DCF 251.04(2)(h)9., including fire and tornado evacuation plans and the operation of fire extinguishers 251.05(4)(a)3.; 251.06(3); 251.06(4)",
  "Prevention and response to emergencies due to food and allergic reactions 251.05(4)(a)4.",
  "First aid procedures 251.05(4)(a)5.",
  "Administration of medications 251.05(4)(a)6.",
  "Job responsibilities in relation to the job description 251.05(4)(a)7.",
  "Training in the recognition of childhood illnesses and in infectious disease control, including hand washing procedures and universal precautions for handling body fluids 251.05(4)(a)8.",
  "A schedule of activities of the center 251.05(4)(a)9.; 251.07(1)",
  "The procedure for ensuring that all child care workers know the children assigned to their care and their whereabouts at all times, including during center-provided transportation 251.05(4)(a)11.; 251.055(1)(f)",
  "Child management techniques 251.05(4)(a)12.; 251.07(2)",
  "The procedure for sharing information related to a child's special health care needs, including any physical, emotional, social, or cognitive disabilities with any child care worker who may be assigned to care for that child throughout the day 251.05(4)(a)13.",
  "A review of procedures to reduce the risk of sudden infant death syndrome (SIDS) prior to an employee's or volunteer's first day of work, if licensed to care for children under 1 year of age 251.05(4)(a)14.",
  "The procedure to contact a parent if a child is absent from the center without prior notification from the parent 251.05(4)(a)15.; 251.055(1)(h)",
  "Information on any special needs that a child enrolled in the center may have and the plan for how those needs will be met 251.05(4)(a)16.",
  "Building and physical premises safety, including identification of and protection from hazards, bodies of water, and vehicular traffic. 251.05(4)(a)17.",
  "The handling and storage of hazardous materials and the appropriate disposal of biocontaminants. 251.05(4)(a)18.",
  "Training in abusive head trauma prevention and impacted babies and appropriate ways to manage crying, fussing or distraught children 251.05(3)(b).",
  "Training in techniques of evacuating sleeping children in an emergency, if the center is licensed to operate between 10:00 p.m. and 5:00 a.m. 251.10(4)(a)."
];

const OrientationChecklistForm = ({ open, onClose, staff, staffName, token }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    trainer_name: '',
    trainer_position: '',
    items: ORIENTATION_ITEMS.map((_, idx) => ({
      id: String(idx + 1),
      staff_initials: '',
      staff_date: '',
      trainer_initials: '',
      trainer_date: ''
    }))
  });

  useEffect(() => {
    if (open && staff?.id) {
      loadData();
    }
  }, [open, staff?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/${staff.id}/orientation-checklist-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        // Merge saved items with default structure
        const savedItems = response.data.items || [];
        const mergedItems = ORIENTATION_ITEMS.map((_, idx) => {
          const savedItem = savedItems.find(item => item.id === String(idx + 1));
          return savedItem || {
            id: String(idx + 1),
            staff_initials: '',
            staff_date: '',
            trainer_initials: '',
            trainer_date: ''
          };
        });
        
        setFormData({
          trainer_name: response.data.trainer_name || '',
          trainer_position: response.data.trainer_position || '',
          items: mergedItems
        });
      }
    } catch (error) {
      console.error('Error loading orientation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/staff/${staff.id}/orientation-checklist-data`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Orientation checklist saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save orientation checklist');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/staff/${staff.id}/forms/dcf-2026`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DCF-F-CFS2026_${staffName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(`${API}/staff/${staff.id}/forms/dcf-2026`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      printWindow.onload = () => printWindow.print();
    } catch (error) {
      toast.error('Failed to open for printing');
    }
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...formData, items: updatedItems });
  };

  const markAllComplete = () => {
    const today = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const updatedItems = formData.items.map(item => ({
      ...item,
      staff_date: item.staff_date || today,
      trainer_date: item.trainer_date || today
    }));
    setFormData({ ...formData, items: updatedItems });
    toast.success('All items marked with today\'s date');
  };

  const completedCount = formData.items.filter(item => 
    item.staff_initials && item.staff_date && item.trainer_initials && item.trainer_date
  ).length;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="orientation-checklist-form-dialog">
        <DialogHeader>
          <DialogTitle>Staff Orientation Checklist (DCF-F-CFS2026) - {staffName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Staff Info */}
          <div className="grid grid-cols-3 gap-4 text-sm bg-muted/50 p-3 rounded-lg">
            <div><span className="font-medium">Staff Name:</span> {staffName}</div>
            <div><span className="font-medium">Position:</span> {staff?.position || 'N/A'}</div>
            <div><span className="font-medium">Start Date:</span> {staff?.hire_date || 'N/A'}</div>
          </div>

          {/* Trainer Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Trainer Name</Label>
              <Input
                value={formData.trainer_name}
                onChange={(e) => setFormData({ ...formData, trainer_name: e.target.value })}
                placeholder="Enter trainer name"
                data-testid="trainer-name-input"
              />
            </div>
            <div>
              <Label>Trainer Position</Label>
              <Input
                value={formData.trainer_position}
                onChange={(e) => setFormData({ ...formData, trainer_position: e.target.value })}
                placeholder="Enter trainer position"
                data-testid="trainer-position-input"
              />
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">Progress: {completedCount} of {ORIENTATION_ITEMS.length} items completed</span>
            </div>
            <Button size="sm" variant="outline" onClick={markAllComplete}>
              Fill Today's Date for All
            </Button>
          </div>

          {/* Checklist Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-2 w-1/2">Policy / Procedure</th>
                  <th className="text-center p-2 w-[12%]">Staff Initials</th>
                  <th className="text-center p-2 w-[12%]">Staff Date</th>
                  <th className="text-center p-2 w-[12%]">Trainer Initials</th>
                  <th className="text-center p-2 w-[12%]">Trainer Date</th>
                </tr>
              </thead>
              <tbody>
                {ORIENTATION_ITEMS.map((item, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2 text-xs">{idx + 1}. {item}</td>
                    <td className="p-1">
                      <Input
                        className="text-center text-xs h-7"
                        value={formData.items[idx]?.staff_initials || ''}
                        onChange={(e) => updateItem(idx, 'staff_initials', e.target.value.toUpperCase())}
                        placeholder="--"
                        maxLength={3}
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        className="text-center text-xs h-7"
                        value={formData.items[idx]?.staff_date || ''}
                        onChange={(e) => updateItem(idx, 'staff_date', e.target.value)}
                        placeholder="mm/dd/yy"
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        className="text-center text-xs h-7"
                        value={formData.items[idx]?.trainer_initials || ''}
                        onChange={(e) => updateItem(idx, 'trainer_initials', e.target.value.toUpperCase())}
                        placeholder="--"
                        maxLength={3}
                      />
                    </td>
                    <td className="p-1">
                      <Input
                        className="text-center text-xs h-7"
                        value={formData.items[idx]?.trainer_date || ''}
                        onChange={(e) => updateItem(idx, 'trainer_date', e.target.value)}
                        placeholder="mm/dd/yy"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter className="flex justify-between gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint} data-testid="print-orientation">
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button variant="outline" onClick={handleDownload} data-testid="download-orientation">
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-orientation">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrientationChecklistForm;
